import { useEffect, useRef } from 'react';

import { useAppDispatch, useAppSelector } from '@/store';
import { PREFERENCE_KEYS, saveUserPreferences } from '@/store/slices/userPreferences';
import { clearStartTracking, readStartTracking, type StartTracking } from '@/utils/start-tracking';

import { isPrefsReliablyLoaded } from './isPrefsReliablyLoaded';

/**
 * Persist the marketing-site ad-attribution snapshot (`startProperties.tracking`) to the
 * cross-device user-preferences store — ONCE per account, app-wide.
 *
 * Mounted in `OwsTheme` (the only provider that wraps every route), NOT in the configurator, so the
 * first-touch snapshot lands as soon as an authenticated identity + a loaded prefs snapshot exist —
 * independent of whether the owner ever reaches the onboarding wizard (an existing owner who clicks
 * an ad, a lead who bounces mid-flow, etc. are all covered).
 *
 * Durability: the snapshot is read from the durable `ows-start-tracking` sessionStorage key that the
 * boot shim (`src/dev.js`) writes, so it survives the auth-gate `/login` redirect, registration, the
 * session mint, reloads, AND the one-shot raw-`startProperties` stash being consumed by the
 * configurator (see `src/utils/start-tracking.ts`). The key is cleared only AFTER the save lands, so
 * a failed POST simply retries on the next authenticated load.
 *
 * Immutable first-touch (spec §5.3 / §7.2): we only write when the account has no snapshot yet, so a
 * returning visitor can't clobber the original attribution — which is why we wait for prefs to LOAD
 * before deciding. Stored regardless of marketing consent: it's first-party operational attribution
 * data, not a pixel fire (spec §7.5).
 */
export function usePersistStartTracking(): void {
  const dispatch = useAppDispatch();
  // Gate on a RESOLVED identity (a `user_id`), like the rest of OwsTheme — the POST associates with
  // the user server-side, and a bare boot token before `/auth/check` resolves would mis-tag it.
  const authenticated = useAppSelector((s) => s.authentication.user.user_id != null);
  const prefsLoaded = useAppSelector((s) => isPrefsReliablyLoaded(s.userPreferences));
  const trackingAlreadyStored = useAppSelector((s) => s.userPreferences.preferences[PREFERENCE_KEYS.tracking] != null);

  // Read the durable snapshot exactly once — sessionStorage is stable for the session, and the key
  // isn't cleared until after we persist, so this survives an OwsTheme remount.
  const trackingRef = useRef<StartTracking | null | undefined>(undefined);
  if (trackingRef.current === undefined) trackingRef.current = readStartTracking();

  // Latest "already stored" flag via a ref so it is NOT an effect dependency: it flips false→true as
  // a RESULT of our own save (the reducer optimistically merges the snapshot back in), and re-running
  // this one-shot effect on that echo is pointless — the done-ref already guards the dispatch.
  const alreadyStoredRef = useRef(trackingAlreadyStored);
  alreadyStoredRef.current = trackingAlreadyStored;

  const doneRef = useRef(false);
  useEffect(() => {
    if (doneRef.current) return;
    const tracking = trackingRef.current;
    if (!tracking) {
      doneRef.current = true; // no hand-off — never revisit
      return;
    }
    if (!authenticated || !prefsLoaded) return; // wait for identity + a loaded snapshot (immutable check)
    doneRef.current = true;
    if (alreadyStoredRef.current) {
      clearStartTracking(); // first-touch already captured — consume the durable key, never overwrite
      return;
    }
    dispatch(saveUserPreferences({ [PREFERENCE_KEYS.tracking]: tracking }))
      .unwrap()
      .then(() => clearStartTracking())
      .catch(() => {
        /* save failed — leave the durable key so the next authenticated load retries the persist. */
      });
  }, [authenticated, prefsLoaded, dispatch]);
}

export default usePersistStartTracking;
